import { createContext, useState, useEffect } from "react";
import jwt_decode from "jwt-decode";
import { useNavigate } from "react-router-dom";
import axios from "src/api/axios";
import { getCookie, setCookie, removeCookie } from "./index";
import axiosLib from "axios";
import { BASE_URL } from "../config/config";

const AuthContext = createContext();

// eslint-disable-next-line react/prop-types
export const AuthContextProvider = ({ children }) => {
  const navigate = useNavigate();

  // Check if we have a valid (non-expired) access token
  const getInitialUser = () => {
    let tokenStr = getCookie("authToken");
    if (tokenStr) {
      let token = tokenStr;
      try {
        const parsed = JSON.parse(tokenStr);
        if (parsed && parsed.token) token = parsed.token;
      } catch (e) {}

      if (!token) {
        return null;
      }
      try {
        const decodedToken = jwt_decode(token);
        let currentDate = new Date();
        // JWT exp is in seconds
        if (decodedToken.exp * 1000 < currentDate.getTime()) {
          // Token expired — but DON'T return null yet if refresh token exists
          return null;
        } else {
          return decodedToken;
        }
      } catch (e) {
        return null;
      }
    }
    return null;
  };

  const [user, setUser] = useState(getInitialUser);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // On mount: if access token is expired but refresh token exists, try to refresh
  useEffect(() => {
    const tryRefresh = async () => {
      const refreshToken = getCookie("refreshToken");
      if (!refreshToken) return; // no refresh token, nothing to do

      // Only refresh if user is null (i.e., access token is missing or expired)
      if (user) return; // user already set from valid access token

      setIsRefreshing(true);
      try {
        const response = await axiosLib.post(
          `${BASE_URL}admin/refresh-token`,
          { refreshToken },
          { withCredentials: true }
        );

        const { token: newToken, refreshToken: newRefreshToken } = response.data;

        // Update cookies
        setCookie("authToken", newToken, 1);
        if (newRefreshToken) {
          setCookie("refreshToken", newRefreshToken, 7);
        }
        const decoded = jwt_decode(newToken);
        setCookie("user", JSON.stringify(decoded), 1);
        setUser(decoded);
      } catch (error) {
        // Refresh failed — clear everything
        console.warn("Token refresh failed:", error.message);
        removeCookie("authToken");
        removeCookie("refreshToken");
        removeCookie("user");
        setUser(null);
      } finally {
        setIsRefreshing(false);
      }
    };

    tryRefresh();
  }, []);

  const login = async (tokenObj) => {
    setCookie("authToken", tokenObj.token, 1); // 1 day
    // Store refresh token separately
    if (tokenObj.refreshToken) {
      setCookie("refreshToken", tokenObj.refreshToken, 7); // 7 days
    }
    setCookie("user", JSON.stringify(jwt_decode(tokenObj.token)), 1);
    setUser(jwt_decode(tokenObj.token));
  };

  const logout = async () => {
    // F-07: Call backend logout API to blocklist the token
    try {
      const authData = getCookie("authToken");
      if (authData) {
        let token = authData;
        try {
          const parsed = JSON.parse(authData);
          if (parsed && parsed.token) token = parsed.token;
        } catch (e) {}

        if (token) {
          await axios.post('admin/logout', {});
        }
      }
    } catch (error) {
      // Logout should succeed even if API call fails
      console.warn('Logout API call failed:', error.message);
    }
    removeCookie("authToken");
    removeCookie("refreshToken");
    removeCookie("user");
    // Also remove legacy/alternate cookie names
    removeCookie("access_token_admin");
    removeCookie("refresh_token");
    removeCookie("refreshtoken");
    removeCookie("users");
    setUser(null);
    navigate("/login");
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, isRefreshing }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;

